import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import { getAuthHeaders } from '../../auth';
import { usePermissions } from '../../services/PermissionsContext';
import useSortableTable from '../common/useSortableTable';

const API = '/api/admin/notifications/';

const fmtDate = (d) => (d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-');

const cardColors = [
  { bg: '#dbeafe', border: '#93c5fd', text: '#1e40af' },
  { bg: '#dcfce7', border: '#86efac', text: '#166534' },
  { bg: '#fef9c3', border: '#fde047', text: '#854d0e' },
  { bg: '#fce7f3', border: '#f9a8d4', text: '#9d174d' },
  { bg: '#ede9fe', border: '#c4b5fd', text: '#5b21b6' },
  { bg: '#ffedd5', border: '#fdba74', text: '#9a3412' },
  { bg: '#cffafe', border: '#67e8f9', text: '#155e75' },
  { bg: '#f0fdf4', border: '#4ade80', text: '#14532d' },
];

const fmtDetails = (text) => {
  const t = (text || '').toString().trim();
  if (!t) return '-';
  return t.length > 50 ? t.slice(0, 50) + '.....' : t;
};

const Notifications = () => {
  const { hasPermission } = usePermissions();
  const canView = hasPermission('NOTIFICATIONS', 'can_view');

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState('');

  const { items: sortedRows, requestSort, getSortIndicator } = useSortableTable(
    rows,
    { key: 'acknowledged_on', direction: 'descending' },
    (row, key) => {
      if (key === 'acknowledged_on') {
        const val = row[key];
        if (!val) return null;
        const t = new Date(val).getTime();
        return isNaN(t) ? null : t;
      }
      return row[key];
    }
  );

  const countsByDepartment = useMemo(() => sortedRows.reduce((acc, row) => {
    const dept = row.department_name || 'UNKNOWN';
    acc[dept] = (acc[dept] || 0) + 1;
    return acc;
  }, {}), [sortedRows]);

  const departments = useMemo(() => Object.keys(countsByDepartment).sort(), [countsByDepartment]);

  const rowsByDepartment = useMemo(() => sortedRows.reduce((acc, row) => {
    const dept = row.department_name || 'UNKNOWN';
    if (!acc[dept]) acc[dept] = [];
    acc[dept].push(row);
    return acc;
  }, {}), [sortedRows]);

  useEffect(() => {
    if (departments.length === 0) return;
    if (selectedDepartment === '' || (selectedDepartment !== '*' && !departments.includes(selectedDepartment))) {
      setSelectedDepartment(departments[0]);
    }
  }, [departments, selectedDepartment]);

  const displayRows = selectedDepartment === '*' ? sortedRows : (rowsByDepartment[selectedDepartment] || []);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(API, { headers: getAuthHeaders(false) });
      if (!res.ok) throw new Error('Failed to load notifications');
      const data = await res.json();
      setRows(Array.isArray(data) ? data : data.results || []);
    } catch (e) {
      toast.error(e.message || 'Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

  useEffect(() => {
    const handleAck = () => fetchNotifications();
    const handleFund = () => fetchNotifications();
    window.addEventListener('notification-acknowledged', handleAck);
    window.addEventListener('fund-request-notification-sent', handleFund);
    const t = setInterval(fetchNotifications, 60000);
    return () => {
      window.removeEventListener('notification-acknowledged', handleAck);
      window.removeEventListener('fund-request-notification-sent', handleFund);
      clearInterval(t);
    };
  }, [fetchNotifications]);

  if (!canView) {
    return <div style={{ padding: 20 }}><h2>Permission Denied</h2></div>;
  }

  return (
    <div style={{ padding: 16 }}>
      <div className="list-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h2 style={{ margin: 0, color: '#333' }}>Notifications</h2>
        <button className="add-btn" style={{ background: '#3f6b32' }} onClick={fetchNotifications} disabled={loading}>Refresh</button>
      </div>

      {loading && <div className="loading-indicator">Loading...</div>}

      {!loading && rows.length === 0 && (
        <div className="no-data" style={{ padding: 24, textAlign: 'center', background: '#f8f9fa', borderRadius: 6 }}>No notifications found.</div>
      )}

      {!loading && rows.length > 0 && (
        <>
          {(() => {
            const allActive = selectedDepartment === '*';
            const totalCount = sortedRows.length;
            return (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16, justifyContent: 'center' }}>
                <button
                  onClick={() => setSelectedDepartment('*')}
                  style={{
                    background: allActive ? '#374151' : '#f3f4f6',
                    border: '2px solid #9ca3af',
                    borderRadius: 8,
                    padding: '6px 14px',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1,
                    minWidth: 70,
                    outline: allActive ? '2px solid #374151' : 'none',
                    outlineOffset: 2,
                  }}
                >
                  <span style={{ fontSize: 10, color: allActive ? '#e0e0e0' : '#374151', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>ALL</span>
                  <span style={{ fontWeight: 'bold', fontSize: 14, color: allActive ? '#fff' : '#374151', lineHeight: 1 }}>{totalCount}</span>
                </button>
                {departments.map((dept, i) => {
                  const c = cardColors[i % cardColors.length];
                  const isActive = selectedDepartment === dept;
                  return (
                    <button
                      key={dept}
                      onClick={() => setSelectedDepartment(isActive ? '*' : dept)}
                      style={{
                        background: isActive ? c.text : c.bg,
                        border: `2px solid ${c.border}`,
                        borderRadius: 8,
                        padding: '6px 14px',
                        cursor: 'pointer',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 1,
                        minWidth: 70,
                        transition: 'background 0.15s, color 0.15s',
                        outline: isActive ? `2px solid ${c.text}` : 'none',
                        outlineOffset: 2,
                      }}
                    >
                      <span style={{ fontSize: 10, color: isActive ? '#e0e0e0' : c.text, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
                        {dept}
                      </span>
                      <span style={{ fontWeight: 'bold', fontSize: 14, color: isActive ? '#fff' : c.text, lineHeight: 1 }}>
                        {countsByDepartment[dept]}
                      </span>
                    </button>
                  );
                })}
              </div>
            );
          })()}

          <div style={{ fontWeight: 'bold', marginBottom: 8, background: '#f59393', borderRadius: 6, padding: 8, fontSize: 16, textTransform: 'uppercase' }}>
            {selectedDepartment === '*' ? 'ALL NOTIFICATIONS' : `${selectedDepartment} NOTIFICATIONS`}
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table className="data-table" style={{ width: '100%', textTransform: 'uppercase' }}>
              <thead>
                <tr>
                  <th style={{ cursor: 'pointer' }} onClick={() => requestSort('title')}>Notification Title {getSortIndicator('title')}</th>
                  <th>Details</th>
                  <th style={{ cursor: 'pointer' }} onClick={() => requestSort('triggered_by_name')}>Triggered By {getSortIndicator('triggered_by_name')}</th>
                  <th style={{ cursor: 'pointer' }} onClick={() => requestSort('triggered_on')}>Triggered On {getSortIndicator('triggered_on')}</th>
                  <th style={{ cursor: 'pointer' }} onClick={() => requestSort('department_name')}>Dept. Triggered For {getSortIndicator('department_name')}</th>
                  <th style={{ cursor: 'pointer' }} onClick={() => requestSort('user_name')}>User Triggered For {getSortIndicator('user_name')}</th>
                  <th style={{ cursor: 'pointer' }} onClick={() => requestSort('acknowledged_by_name')}>Acknowledged By {getSortIndicator('acknowledged_by_name')}</th>
                  <th style={{ cursor: 'pointer' }} onClick={() => requestSort('acknowledged_on')}>Acknowledged On {getSortIndicator('acknowledged_on')}</th>
                </tr>
              </thead>
              <tbody>
                {displayRows.map((row) => (
                  <tr key={row.id} style={{ background: row.acknowledged_by ? '#f0fdf4' : '#fff' }}>
                    <td style={{ fontWeight: 600 }}>{row.title}</td>
                    <td style={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.message || ''}>
                      {fmtDetails(row.message)}
                    </td>
                    <td>{row.triggered_by_name || '-'}</td>
                    <td>{fmtDate(row.triggered_on)}</td>
                    <td>{row.department_name || '-'}</td>
                    <td>{row.user_name || '-'}</td>
                    <td>{row.acknowledged_by_name || '-'}</td>
                    <td>{fmtDate(row.acknowledged_on)}</td>
                  </tr>
                ))}
                {displayRows.length === 0 && (
                  <tr><td colSpan={8} style={{ textAlign: 'center', padding: 24 }}>No notifications found for this selection.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default Notifications;
